/**
 * Internal time: log a block of admin, training or meeting time, and see
 * the period's own entries. Two exports behind two doors: InternalTimeForm
 * (the form) and InternalTimeEntries (the list). Home's Your day band
 * composes both in InternalTimeCard; My time shows the form in its Log
 * internal time dialog and the list under the Internal breakdown of its
 * summary card.
 *
 * The form posts kind, hours and the date, with an optional one-line
 * note. Hours step on the tenant's billing increment, the same grid as
 * the note composer, and an off-grid value gets the composer's own line
 * before anything is sent; the server refuses the same value the same
 * way. A save calls back (onSaved, or the error line) only while the
 * form is still mounted, so a dialog closed mid-save is left alone and
 * the refresh still runs.
 * Each entry in the period lists with Edit and Remove while the period
 * is open, its hours to one decimal; once it has gone to payroll the
 * list stays and the controls go, replaced by one quiet line. A tenant
 * with no payroll calendar yet is told so in the same quiet register and
 * never locks. Input values (the Hours field, the Edit pre-fill) keep
 * the stored value exactly.
 */

import { type FormEvent, type ReactNode, type RefObject, useEffect, useState } from 'react';
import { Button } from '@/components/primitives/Button';
import { Field } from '@/components/primitives/Field';
import { FilterPill } from '@/components/primitives/FilterPill';
import { FLD } from '@/components/primitives/modalFormStyles';
import { formatHoursTenths } from '@/lib/hours';
import { hoursIncrementMessage, isOnHoursIncrement } from '@/lib/hoursIncrement';
import { MONTHS, WEEKDAYS, parseIsoDate } from '@/lib/proseDate';
import { useSupportContact } from '@/hooks/useSupportContact';
import {
  TIME_ENTRY_KINDS,
  TIME_ENTRY_KIND_LABEL,
  timeEntryErrorMessage,
  useCreateTimeEntry,
  useDeleteTimeEntry,
  useTimeEntries,
  useUpdateTimeEntry,
  type HoursPeriodWire,
  type TimeEntryInput,
  type TimeEntryKind,
  type TimeEntryWire,
} from '@/lib/timeEntriesApi';
import { DateInput } from '@/components/primitives/DateInput';

export interface InternalTimeCardProps {
  tenantSlug: string;
  period: HoursPeriodWire;
  /** false until the office sets the payroll anchor. */
  configPresent: boolean;
  /** billing.hours_increment, the step the hours input moves in. */
  hoursIncrement: string;
  /** Injected by the page, never Date.now() here. */
  todayIso: string;
  /** The hours input, so a page-level "+ Internal Time" can focus it. */
  hoursInputRef?: RefObject<HTMLInputElement | null>;
}

export interface InternalTimeFormProps extends InternalTimeCardProps {
  /** Runs after a save lands; the dialog closes on it. */
  onSaved?: () => void;
  /** Given, the actions row is the dialog's: Cancel (Close on a locked period) then Log time, right-aligned. */
  onCancel?: () => void;
}

export interface InternalTimeEntriesProps {
  tenantSlug: string;
  period: HoursPeriodWire;
  hoursIncrement: string;
  todayIso: string;
}

/** "Mon 2 Nov". */
export function entryDateLabel(iso: string): string {
  const d = parseIsoDate(iso);
  return `${WEEKDAYS[d.getDay()]!.slice(0, 3)} ${d.getDate()} ${MONTHS[d.getMonth()]!.slice(0, 3)}`;
}

interface EntryFormState {
  kind: TimeEntryKind;
  hours: string;
  occurredOn: string;
  note: string;
}

function toInput(s: EntryFormState): TimeEntryInput {
  return {
    kind: s.kind,
    hours: Number(s.hours),
    occurred_on: s.occurredOn,
    note: s.note.trim() === '' ? null : s.note.trim(),
  };
}

interface EntryFormProps {
  idPrefix: string;
  state: EntryFormState;
  onChange: (next: EntryFormState) => void;
  hoursIncrement: string;
  todayIso: string;
  hoursInputRef?: RefObject<HTMLInputElement | null>;
}

function EntryForm({ idPrefix, state, onChange, hoursIncrement, todayIso, hoursInputRef }: EntryFormProps): ReactNode {
  return (
    <div className="flex flex-col gap-3">
      <div role="group" aria-label="Kind" className="flex flex-wrap gap-2" data-testid={`${idPrefix}-kinds`}>
        {TIME_ENTRY_KINDS.map((k) => (
          <FilterPill
            key={k}
            label={TIME_ENTRY_KIND_LABEL[k]}
            active={state.kind === k}
            data-testid={`${idPrefix}-kind-${k}`}
            onClick={() => onChange({ ...state, kind: k })}
          />
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
        <Field label="Hours" htmlFor={`${idPrefix}-hours`}>
          <input
            id={`${idPrefix}-hours`}
            ref={hoursInputRef}
            data-testid={`${idPrefix}-hours`}
            className={`${FLD} w-24`}
            type="number"
            inputMode="decimal"
            min={0}
            step={Number(hoursIncrement)}
            value={state.hours}
            onChange={(e) => onChange({ ...state, hours: e.target.value })}
          />
        </Field>
        <div className="min-w-0 max-w-full">
          <Field label="Date" htmlFor={`${idPrefix}-date`}>
            <DateInput
              id={`${idPrefix}-date`}
              data-testid={`${idPrefix}-date`}
              size="field" className="w-full max-w-[10rem] min-w-0"
              max={todayIso}
              value={state.occurredOn}
              onChange={(e) => onChange({ ...state, occurredOn: e.target.value })}
            />
          </Field>
        </div>
      </div>
      <Field label="Note" htmlFor={`${idPrefix}-note`} help="One line.">
        <input
          id={`${idPrefix}-note`}
          data-testid={`${idPrefix}-note`}
          className={FLD}
          type="text"
          maxLength={200}
          placeholder="Quarterly training"
          value={state.note}
          onChange={(e) => onChange({ ...state, note: e.target.value })}
        />
      </Field>
    </div>
  );
}

function fresh(todayIso: string, kind: TimeEntryKind = 'admin'): EntryFormState {
  return { kind, hours: '', occurredOn: todayIso, note: '' };
}

/** The log form while the period is open, or the locked line once it has gone to payroll. */
export function InternalTimeForm({
  tenantSlug,
  period,
  hoursIncrement,
  todayIso,
  hoursInputRef,
  onSaved,
  onCancel,
}: InternalTimeFormProps): ReactNode {
  const create = useCreateTimeEntry(tenantSlug);
  const supportContact = useSupportContact({ enabled: false });
  const [form, setForm] = useState<EntryFormState>(() => fresh(todayIso));
  const [formError, setFormError] = useState<string | null>(null);

  const hoursNum = Number(form.hours);
  const hoursPresent = form.hours.trim() !== '' && Number.isFinite(hoursNum) && hoursNum > 0;
  const hoursOffStep = hoursPresent && !isOnHoursIncrement(hoursNum, hoursIncrement);
  const canSubmit = hoursPresent && !hoursOffStep && form.occurredOn !== '' && !create.isPending;

  // The per-call callbacks run only while this form is still mounted, so
  // a save that lands after the dialog closed neither closes nor writes
  // into a form opened since; the hook's own onSuccess still refreshes
  // the totals and the list.
  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    setFormError(null);
    const input = toInput(form);
    create.mutate(input, {
      onSuccess: () => {
        setForm(fresh(todayIso, form.kind));
        onSaved?.();
      },
      onError: (err) => setFormError(timeEntryErrorMessage(err, supportContact, input)),
    });
  }

  const submit = (
    <Button type="submit" variant="primary" size="sm" disabled={!canSubmit} loading={create.isPending} data-testid="internal-time-submit">
      Log time
    </Button>
  );

  return (
    <>
      {period.open ? (
        <form onSubmit={onSubmit} data-testid="internal-time-form" className="flex flex-col gap-3">
          <EntryForm
            idPrefix="internal-time"
            state={form}
            onChange={setForm}
            hoursIncrement={hoursIncrement}
            todayIso={todayIso}
            hoursInputRef={hoursInputRef}
          />
          {hoursOffStep && (
            <p data-testid="internal-time-hours-nudge" className="text-xs text-ink-soft">
              {hoursIncrementMessage(hoursIncrement, hoursNum)}
            </p>
          )}
          {formError !== null && (
            <p role="alert" data-testid="internal-time-error" className="text-sm text-warm-700">
              {formError}
            </p>
          )}
          {onCancel ? (
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" size="sm" data-testid="internal-time-cancel" onClick={() => onCancel()}>
                Cancel
              </Button>
              {submit}
            </div>
          ) : (
            <div>{submit}</div>
          )}
        </form>
      ) : (
        <>
          <p data-testid="internal-time-locked" className="text-sm text-ink-soft">
            Locked for payroll. This period could be changed until {period.editable_until === null ? 'it closed' : entryDateLabel(period.editable_until)}; ask the office if something is wrong.
          </p>
          {onCancel && (
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" size="sm" data-testid="internal-time-cancel" onClick={() => onCancel()}>
                Close
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}

/** The period's own entries, with Edit in place and a two-step Remove while the period is open. */
export function InternalTimeEntries({ tenantSlug, period, hoursIncrement, todayIso }: InternalTimeEntriesProps): ReactNode {
  const { entries, isLoading, isError } = useTimeEntries(tenantSlug, period.start, period.end);
  const update = useUpdateTimeEntry(tenantSlug);
  const remove = useDeleteTimeEntry(tenantSlug);
  const supportContact = useSupportContact({ enabled: false });

  const [editing, setEditing] = useState<{ id: string; state: EntryFormState } | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  // Leaving an open period drops any half-done edit with it.
  useEffect(() => {
    setEditing(null);
    setConfirmRemove(null);
  }, [period.start]);

  async function onSaveEdit(): Promise<void> {
    if (editing === null) return;
    setEditError(null);
    const patch = toInput(editing.state);
    try {
      await update.mutateAsync({ id: editing.id, patch });
      setEditing(null);
    } catch (err) {
      setEditError(timeEntryErrorMessage(err, supportContact, patch));
    }
  }

  async function onRemove(id: string): Promise<void> {
    setEditError(null);
    try {
      await remove.mutateAsync(id);
      setConfirmRemove(null);
    } catch (err) {
      setEditError(timeEntryErrorMessage(err, supportContact));
    }
  }

  return (
    <div data-testid="internal-time-entries" className="@container mt-4 border-t border-sage-100 pt-3">
      <h3 className="mb-1.5 text-label font-semibold uppercase tracking-label text-sage-400">Internal time this period</h3>
      {isLoading ? (
        <p className="text-sm text-ink-soft">Loading.</p>
      ) : isError ? (
        <p className="text-sm text-warm-700">Could not load your entries.</p>
      ) : entries.length === 0 ? (
        <p data-testid="internal-time-empty" className="text-sm text-ink-soft">
          Log admin, training or meeting time and you'll see it here.
        </p>
      ) : (
        <ul className="m-0 list-none divide-y divide-sage-100 p-0">
          {entries.map((entry: TimeEntryWire) => (
            <li key={entry.id} data-testid="internal-time-entry" className={editing?.id === entry.id ? 'py-2.5' : 'py-2 first:pt-0.5'}>
              {editing?.id === entry.id ? (
                <div className="flex flex-col gap-3" data-testid="internal-time-edit">
                  <EntryForm
                    idPrefix={`internal-time-edit-${entry.id}`}
                    state={editing.state}
                    onChange={(state) => setEditing({ id: entry.id, state })}
                    hoursIncrement={hoursIncrement}
                    todayIso={todayIso}
                  />
                  {editError !== null && (
                    <p role="alert" data-testid="internal-time-edit-error" className="text-sm text-warm-700">
                      {editError}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <Button variant="primary" size="sm" onClick={() => void onSaveEdit()} loading={update.isPending} data-testid="internal-time-edit-save">
                      Save
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditing(null)} data-testid="internal-time-edit-cancel">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                // One row: date, kind, the note, Edit and Remove, hours flush
                // right. Three widths of the list itself (container queries,
                // so the half-width breakdown column counts as narrow, and
                // the steps grow with the reader's text size):
                //   - from 28rem, one line; the note truncates (full text on
                //     hover), and the date and kind have fixed widths and the
                //     hours a minimum, so the columns line up row to row;
                //   - from 20rem, the note takes the line under the kind, in
                //     full, and Edit, Remove and the hours stay on the first;
                //   - below that, Edit and Remove take a line of their own
                //     under the note, and the kind may wrap under the date,
                //     so nothing crosses the card's edge at any width.
                // On the first line the actions take no height of their own
                // (-my-1 against the button's min-h-7), so the row keeps the
                // By client and Internal lines' rhythm; on a line of their
                // own they keep their full height, clear of the note. Reading order: date, kind, note,
                // hours, then Edit and Remove.
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-2 gap-y-0.5 @xs:grid-cols-[minmax(0,1fr)_auto_auto] @md:grid-cols-[auto_minmax(0,1fr)_auto_auto] @md:gap-x-2.5">
                  <div data-testid="internal-time-entry-lead" className="col-start-1 row-start-1 flex min-w-0 flex-wrap items-baseline gap-x-2">
                    <span data-testid="internal-time-entry-date" className="w-[4.75rem] shrink-0 whitespace-nowrap text-sm tabular-nums text-ink-soft">
                      {entryDateLabel(entry.occurred_on)}
                    </span>
                    <p data-testid="internal-time-entry-kind" className="shrink-0 whitespace-nowrap text-value font-medium text-ink @md:w-20">
                      {TIME_ENTRY_KIND_LABEL[entry.kind]}
                    </p>
                  </div>
                  {entry.note !== null && (
                    <p
                      className="col-[1/-1] row-start-2 min-w-0 break-words text-xs text-ink-soft @min-[16rem]:pl-[5.25rem] @md:col-[2/3] @md:row-start-1 @md:truncate @md:pl-0"
                      data-testid="internal-time-entry-note"
                      title={entry.note}
                    >
                      {entry.note}
                    </p>
                  )}
                  <span data-testid="internal-time-entry-hours" className="col-start-2 row-start-1 min-w-[2.75rem] justify-self-end whitespace-nowrap text-right text-sm tabular-nums text-ink @xs:col-start-3 @md:col-start-4">
                    {formatHoursTenths(entry.hours)} h
                  </span>
                  {period.open && confirmRemove !== entry.id && (
                    <div data-testid="internal-time-entry-actions" className="col-[1/-1] row-start-3 flex items-center gap-1 @min-[16rem]:pl-[4.875rem] @xs:col-[2/3] @xs:row-start-1 @xs:-my-1 @xs:pl-0 @md:col-[3/4]">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="!px-1.5"
                        data-testid="internal-time-edit-button"
                        onClick={() => {
                          setEditError(null);
                          setConfirmRemove(null);
                          setEditing({
                            id: entry.id,
                            state: { kind: entry.kind, hours: entry.hours, occurredOn: entry.occurred_on, note: entry.note ?? '' },
                          });
                        }}
                      >
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" className="!px-1.5" data-testid="internal-time-remove-button" onClick={() => setConfirmRemove(entry.id)}>
                        Remove
                      </Button>
                    </div>
                  )}
                  {period.open && confirmRemove === entry.id && (
                    <div className="col-[1/-1] flex flex-wrap items-center gap-1 @min-[16rem]:pl-[5.25rem]">
                      <span className="whitespace-nowrap text-xs text-ink-soft">Remove this entry?</span>
                      <span className="flex items-center gap-1">
                        <Button variant="danger" size="sm" onClick={() => void onRemove(entry.id)} loading={remove.isPending} data-testid="internal-time-remove-confirm">
                          Remove
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmRemove(null)} data-testid="internal-time-remove-cancel">
                          Keep
                        </Button>
                      </span>
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {editError !== null && editing === null && (
        <p role="alert" data-testid="internal-time-list-error" className="mt-2 text-sm text-warm-700">
          {editError}
        </p>
      )}
    </div>
  );
}

/** Home's card: the form over the period's entries, in one surface. */
export function InternalTimeCard({
  tenantSlug,
  period,
  configPresent,
  hoursIncrement,
  todayIso,
  hoursInputRef,
}: InternalTimeCardProps): ReactNode {
  return (
    <div
      data-testid="internal-time-card"
      className="rounded-card border border-sage-200 bg-paper px-4 py-4 shadow-sh1"
    >
      <InternalTimeForm
        tenantSlug={tenantSlug}
        period={period}
        configPresent={configPresent}
        hoursIncrement={hoursIncrement}
        todayIso={todayIso}
        hoursInputRef={hoursInputRef}
      />
      <InternalTimeEntries tenantSlug={tenantSlug} period={period} hoursIncrement={hoursIncrement} todayIso={todayIso} />
    </div>
  );
}
